import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactFormCardRefs
 * @property {HTMLFormElement} contactForm - Shopify contact form that emails the store
 * @property {HTMLFormElement} customerForm - Hidden Shopify customer form that creates the customer
 * @property {HTMLInputElement} [consent] - Opt-in checkbox for email updates
 * @property {HTMLButtonElement} submitButton - Contact form submit button
 */

/**
 * Sends the contact form as usual. When the visitor opts in to email updates,
 * first posts the hidden customer form so Shopify creates (or updates) a customer
 * with the entered name, email and tags. Shopify subscribes customers created by
 * this form to email marketing, so it only runs when the checkbox is ticked.
 *
 * Without JavaScript the contact form still sends, and the opt-in shows in the
 * email the store receives.
 *
 * @extends {Component<ContactFormCardRefs>}
 */
class ContactFormCard extends Component {
  requiredRefs = ['contactForm', 'customerForm', 'submitButton'];

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    const { contactForm, customerForm, consent, submitButton } = this.refs;

    if (event.target !== contactForm) return;
    if (!consent?.checked) return;

    event.preventDefault();
    submitButton.disabled = true;

    try {
      this.#fillCustomerForm();

      const response = await fetch(customerForm.action, {
        method: 'POST',
        body: new FormData(customerForm),
      });

      if (!response.ok || new URL(response.url).pathname.startsWith('/challenge')) {
        throw new Error(`Customer form was not accepted (status ${response.status}, ${response.url})`);
      }
    } catch (error) {
      console.error('Failed to create customer from contact form:', error);
    } finally {
      // Always send the message, even if creating the customer failed
      contactForm.submit();
    }
  }

  #fillCustomerForm() {
    const { contactForm, customerForm } = this.refs;
    const contactData = new FormData(contactForm);
    const name = String(contactData.get('contact[name]') ?? '').trim();
    const [firstName = '', ...lastNames] = name.split(/\s+/);
    const topic = String(contactData.get('contact[Topic]') ?? '').trim();
    const tags = [customerForm.dataset.tags, topic && `topic:${topic}`].filter(Boolean).join(', ');

    /** @type {Record<string, string>} */
    const values = {
      'contact[email]': String(contactData.get('contact[email]') ?? '').trim(),
      'contact[first_name]': firstName,
      'contact[last_name]': lastNames.join(' '),
      'contact[tags]': tags,
    };

    for (const [name, value] of Object.entries(values)) {
      const input = customerForm.elements.namedItem(name);

      if (input instanceof HTMLInputElement) input.value = value;
    }
  }
}

if (!customElements.get('contact-form-card-component')) {
  customElements.define('contact-form-card-component', ContactFormCard);
}
